/**
 * Address Modal Component
 *
 * Modal dialog for adding or editing an address.
 * Supports all standard address fields and types.
 */

import { Modal } from '../ui'
import {
  AddressLabelTypeFields,
  AddressLineFields,
  AddressCountryStateFields,
  AddressCityPostalFields,
  AddressIndiaFields,
  AddressModalActions,
  AddressPrimaryCheckbox,
  AddressQuickPasteBar,
} from './address-modal/AddressFormFields'
import { useAddressForm } from './address-modal/useAddressForm'

import type { Address } from '../../types/billing'

export interface AddressModalProps {
  /** Whether modal is open */
  isOpen: boolean
  /** Callback to close modal */
  onClose: () => void
  /** Callback when address is saved */
  onSave: (address: Address) => void
  /** Address to edit (null for new address) */
  address?: Address | null
  /** Default address type to pre-select */
  defaultType?: string
}

type AddressFormReturn = ReturnType<typeof useAddressForm>

function HeaderRows({ form }: { form: AddressFormReturn }): JSX.Element {
  const fd = form.formData
  return (
    <>
      <AddressQuickPasteBar
        countryCode={fd.countryCode ?? 'IN'}
        id="address-modal-paste"
        onChange={form.handleChange}
      />
      <AddressLabelTypeFields
        label={fd.label ?? ''}
        labelError={form.errors.label}
        type={fd.type ?? ''}
        typeError={form.errors.type}
        usage={fd.usage ?? []}
        onChange={form.handleChange}
        onUsageChange={(values) => form.handleChange('usage', values)}
      />
      <AddressLineFields
        line1={fd.line1 ?? ''}
        line1Error={form.errors.line1}
        line2={fd.line2 ?? ''}
        onChange={form.handleChange}
      />
    </>
  )
}

function LocationRows({ form }: { form: AddressFormReturn }): JSX.Element {
  const fd = form.formData
  return (
    <>
      <AddressCountryStateFields
        countryCode={fd.countryCode ?? ''}
        countryError={form.errors.country}
        hasStates={form.states.length > 0}
        stateCode={fd.stateCode ?? ''}
        stateError={form.errors.state}
        stateFieldLabel={form.stateFieldLabel}
        onCountryChange={form.handleCountryChange}
        onStateChange={form.handleStateChange}
      />
      <AddressCityPostalFields
        city={fd.city ?? ''}
        cityError={form.errors.city}
        postalCode={fd.postalCode ?? ''}
        postalCodeError={form.errors.postalCode}
        postalCodeExample={form.postalCodeExample}
        postalCodeLabel={form.postalCodeLabel}
        onChange={form.handleChange}
      />
      {fd.countryCode === 'IN' && (
        <AddressIndiaFields
          companyCode={fd.companyCode ?? ''}
          gstin={fd.gstin ?? ''}
          hasStates={form.states.length > 0}
          stateCode={fd.stateCode ?? ''}
          onChange={form.handleChange}
        />
      )}
      <AddressPrimaryCheckbox
        checked={fd.isPrimary ?? false}
        onChange={(checked) => form.handleChange('isPrimary', checked)}
      />
    </>
  )
}

function AddressModalBody({ form }: { form: AddressFormReturn }): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <HeaderRows form={form} />
      <LocationRows form={form} />
    </div>
  )
}

export function AddressModal({
  isOpen,
  onClose,
  onSave,
  address,
  defaultType = 'billing',
}: AddressModalProps): JSX.Element {
  const form = useAddressForm(address, defaultType, isOpen, onSave, onClose)

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      title={address?.id ? 'Edit Address' : 'Add New Address'}
      onClose={form.handleCancel}
    >
      <AddressModalBody form={form} />
      <AddressModalActions
        isEditing={Boolean(address?.id)}
        onCancel={form.handleCancel}
        onSave={form.handleSave}
      />
    </Modal>
  )
}
